import React, { useEffect, useState, useRef } from 'react';
import { CheckIcon, ChevronDownIcon, BuildingIcon, SearchIcon, XIcon, FilterIcon } from 'lucide-react';
export interface Company {
  id: string;
  name: string;
  logo?: string;
}
interface CompanySelectorProps {
  companies: Company[];
  selectedCompanies: Company[];
  onChange: (companies: Company[]) => void;
}
export function CompanySelector({
  companies,
  selectedCompanies,
  onChange
}: CompanySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Filter companies based on search query
  const filteredCompanies = companies.filter(company => company.name.toLowerCase().includes(searchQuery.toLowerCase()));
  // Toggle selection of a company
  const toggleCompany = (company: Company) => {
    const isSelected = selectedCompanies.some(c => c.id === company.id);
    if (isSelected) {
      onChange(selectedCompanies.filter(c => c.id !== company.id));
    } else {
      onChange([...selectedCompanies, company]);
    }
  };
  // Select all companies
  const selectAllCompanies = () => {
    onChange(companies);
  };
  // Clear all selections
  const clearSelections = () => {
    onChange([]);
  };
  // Check if all companies are selected
  const allSelected = companies.length === selectedCompanies.length;
  // Display text based on selection
  const displayText = () => {
    if (selectedCompanies.length === 0) {
      return 'Select Companies';
    } else if (allSelected) {
      return 'All Companies';
    } else if (selectedCompanies.length === 1) {
      return selectedCompanies[0].name;
    } else {
      return `${selectedCompanies.length} Companies Selected`;
    }
  };
  return <div className="relative" ref={dropdownRef} data-id="element-797">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center justify-between min-w-40 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-798">
        <div className="flex items-center" data-id="element-799">
          <BuildingIcon size={16} className="mr-2 text-gray-500" data-id="element-800" />
          <span className="truncate" data-id="element-801">{displayText()}</span>
        </div>
        <ChevronDownIcon size={16} data-id="element-802" />
      </button>
      {isOpen && <div className="absolute right-0 z-10 w-72 mt-1 bg-white border border-gray-200 rounded-md shadow-lg" data-id="element-803">
          {/* Search input */}
          <div className="p-2 border-b border-gray-200" data-id="element-804">
            <div className="relative" data-id="element-805">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-806">
                <SearchIcon size={14} className="text-gray-400" data-id="element-807" />
              </div>
              <input type="text" className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search companies..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-808" />
            </div>
          </div>
          {/* Select all / Clear all */}
          <div className="p-2 border-b border-gray-200 flex justify-between" data-id="element-809">
            <button onClick={selectAllCompanies} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-810">
              Select All
            </button>
            <button onClick={clearSelections} className="text-xs text-gray-600 hover:text-gray-800 font-medium" data-id="element-811">
              Clear All
            </button>
          </div>
          {/* Companies list */}
          <div className="max-h-60 overflow-y-auto" data-id="element-812">
            {filteredCompanies.length > 0 ? filteredCompanies.map(company => {
          const isSelected = selectedCompanies.some(c => c.id === company.id);
          return <div key={company.id} onClick={() => toggleCompany(company)} className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`} data-id="element-813">
                    <div className="flex items-center flex-1" data-id="element-814">
                      {company.logo ? <img src={company.logo} alt={company.name} className="w-6 h-6 rounded-full mr-2" data-id="element-815" /> : <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mr-2" data-id="element-816">
                          <span className="text-xs font-medium text-gray-600" data-id="element-817">
                            {company.name.charAt(0)}
                          </span>
                        </div>}
                      <span className="text-sm" data-id="element-818">{company.name}</span>
                    </div>
                    <div className="ml-2" data-id="element-819">
                      {isSelected ? <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center" data-id="element-820">
                          <CheckIcon size={12} className="text-white" data-id="element-821" />
                        </div> : <div className="w-5 h-5 border border-gray-300 rounded" data-id="element-822"></div>}
                    </div>
                  </div>;
        }) : <div className="px-3 py-2 text-sm text-gray-500" data-id="element-823">
                No companies found
              </div>}
          </div>
          {/* Action buttons */}
          <div className="p-2 border-t border-gray-200 flex justify-end" data-id="element-824">
            <button onClick={() => setIsOpen(false)} className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700" data-id="element-825">
              Apply
            </button>
          </div>
        </div>}
    </div>;
}